import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Team, TeamResponse, TeamsResponse } from './types';
import { ACCOUNTS_TAG } from '../account/constants';
import { ALL_TEAMS_ID, TEAM_STATUSES, TeamPlayerRoles, TEAMS_TAG } from './constants';
import { removeAccountTeam, updateAccountTeam } from '../account';
import { tournamentsApi } from '../tournaments';

export const teamsApi = createApi({
  reducerPath: 'teamsApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/api/v1/teams/' }),
  tagTypes: [TEAMS_TAG, ACCOUNTS_TAG],
  endpoints: (builder) => ({
    getTeams: builder.query<Team[], string>({
      query: (tournamentId: string) => `?tournament_id=${tournamentId}&sorting=players&limit=0`,
      transformResponse: (response: TeamsResponse) => response.data.items,
      providesTags: (result = []) => [...result.map(({ id }) => ({ type: TEAMS_TAG, id } as const)), { type: TEAMS_TAG, id: ALL_TEAMS_ID }],
    }),
    createTeam: builder.mutation<Team, FormData>({
      query: (body: FormData) => ({
        url: '',
        method: 'POST',
        body,
      }),
      transformResponse: (response: TeamResponse) => response.data,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: team } = await queryFulfilled;

          await dispatch(
            teamsApi.util.updateQueryData('getTeams', `${team.tournament_id}`, (draft) => {
              draft.push(team);
            })
          );
          await dispatch(
            teamsApi.util.upsertQueryData('getTeamById', {
              id: `${team.tournament_id}`,
              teamId: `${team.id}`,
            }, { data: team })
          );

          await dispatch(
            tournamentsApi.util.updateQueryData('getTournamentById', `${team.tournament_id}`, (draft) => {
              draft.teams[team.id] = team;
            })
          );

          await updateAccountTeam(team, dispatch);
        } catch { }
      }
    }),
    getTeamById: builder.query<Team, { id: string, teamId: string }>({
      query: ({ id, teamId }) => `${teamId}/?tournament_id=${id}`,
      transformResponse: (response: TeamResponse) => response.data,
      providesTags: (result, error, { teamId }) => [{ type: TEAMS_TAG, id: teamId }],
    }),
    updateTeam: builder.mutation<Team, { body: FormData, teamId: string }>({
      query: ({ body, teamId }) => ({
        url: `${teamId}/`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: TeamResponse) => response.data,
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: team } = await queryFulfilled;

          await dispatch(
            teamsApi.util.updateQueryData('getTeamById', { id: `${team.tournament_id}`, teamId: `${team.id}` }, (draft) => {
              Object.assign(draft, team);
            })
          );
          await updateAccountTeam(team, dispatch);
        } catch { }

      },
    }),
    joinTeam: builder.mutation<Team, { teamId: number, password?: string }>({
      query: ({ teamId, password }) => ({
        url: `${teamId}/players/`,
        method: 'POST',
        body: { password },
      }),
      transformResponse: (response: TeamResponse) => response.data,
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: team } = await queryFulfilled;

          await dispatch(
            teamsApi.util.updateQueryData('getTeamById', { id: `${team.tournament_id}`, teamId: `${team.id}` }, (draft) => {
              Object.assign(draft, team);
            })
          );
          await updateAccountTeam(team, dispatch);
        } catch { }
      },
    }),
    removePlayer: builder.mutation<Team, { id: number, teamId: number, playerId: number }>({
      query: ({ teamId, playerId }) => ({
        url: `${teamId}/players/${playerId}/`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ id, teamId, playerId }, { dispatch, getState, queryFulfilled }) => {
        try {
          await queryFulfilled;

          const { data: team } = teamsApi.endpoints.getTeamById.select({ id: `${id}`, teamId: `${teamId}` })(getState());

          if (team) {
            await dispatch(
              teamsApi.util.updateQueryData('getTeamById', { id: `${id}`, teamId: `${teamId}` }, (draft) => {
                Object.assign(draft, { players: team.players.filter(player => player.id !== playerId) });
              })
            );
          }
        } catch { }
      },
    }),
    disbandTeam: builder.mutation<null, { id: string, teamId: string }>({
      query: ({ teamId }) => ({
        url: `${teamId}/`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ id, teamId }, { dispatch, getState, queryFulfilled }) => {
        try {
          await queryFulfilled;
          const { data: teams = [] } = teamsApi.endpoints.getTeams.select(id)(getState());
          await dispatch(teamsApi.util.upsertQueryData('getTeams', id, {
            data: { items: teams.filter((team) => team.id !== parseInt(teamId)) },
          }));
          await dispatch(tournamentsApi.util.updateQueryData('getTournamentById', `${id}`, (draft) => {
            delete draft.teams[parseInt(teamId)];
          }));
          await removeAccountTeam(id, dispatch);
        } catch { }
      },
      invalidatesTags: (result, error, id) => result ? [{ type: TEAMS_TAG, id: ALL_TEAMS_ID }] : [],
    }),
    changePlayerRole: builder.mutation<Team, { teamId: number, playerId: number, role: TeamPlayerRoles }>({
      query: ({ teamId, playerId, role }) => ({
        url: `${teamId}/players/${playerId}/`,
        method: 'PATCH',
        body: { role },
      }),
      transformResponse: (response: TeamResponse) => response.data,
      invalidatesTags: (result, error, { teamId }) => [{ type: TEAMS_TAG, id: teamId }],
    }),
    markAsReady: builder.mutation<Team, { id: number, teamId: number }>({
      query: ({ teamId }) => ({
        url: `${teamId}/ready/`,
        method: 'POST',
      }),
      transformResponse: (response: TeamResponse) => response.data,
      onQueryStarted: async ({ id, teamId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          await dispatch(
            teamsApi.util.updateQueryData('getTeamById', { id: `${id}`, teamId: `${teamId}` }, (draft) => {
              Object.assign(draft, { status: TEAM_STATUSES.ready });
            })
          );
        } catch { }
      },
    }),
  }),
});

export const {
  useGetTeamsQuery,
  useCreateTeamMutation,
  useGetTeamByIdQuery,
  useUpdateTeamMutation,
  useJoinTeamMutation,
  useRemovePlayerMutation,
  useDisbandTeamMutation,
  useChangePlayerRoleMutation,
  useMarkAsReadyMutation,
} = teamsApi;
